import { Injectable, signal } from "@angular/core";
import { IStyleLegendMetadata, IVectorStyle } from "../model/vector-style-definition.interface";

export type { IStyleLegendMetadata };

export interface IStyleLegendState {
  /** Stable id of the layer whose style the legend is showing; used by the panel for transient toggle-visibility. */
  layerId: string;
  layerName: string;
  /** As originally passed to `show()`. Never mutated for the lifetime of the legend. */
  styleDefinition: IVectorStyle;
  metadata?: IStyleLegendMetadata;
  /**
   * Ids of the legend rows currently toggled off. A row built from the rules has the index of its rule in
   * `styleDefinition.rules` (a `flat` style: only 0); a row built from `metadata.legend` has its position in it.
   * Transient UI state only — never persisted.
   */
  hiddenRowIds: readonly number[];
}

/**
 * Transient UI state for the floating style legend panel — no persistence, no IndexedDB.
 * Written by both the manual "Show legend" trigger (user-layer-data) and the AI
 * apply_layer_style frontend tool (map-frontend-tools), read by StyleLegendPanelComponent.
 * The panel owns the logic that turns `hiddenRowIds` into an actual OL style update;
 * this service is a pure state store and has no view-layer dependencies.
 */
@Injectable({ providedIn: "root" })
export class StyleLegendService {
  readonly state = signal<IStyleLegendState | null>(null);

  show(layerId: string, layerName: string, styleDefinition: IVectorStyle, metadata?: IStyleLegendMetadata): void {
    this.state.set({ layerId, layerName, styleDefinition, metadata, hiddenRowIds: [] });
  }

  setHiddenRowIds(ids: readonly number[]): void {
    const current = this.state();
    if (!current) return;
    this.state.set({ ...current, hiddenRowIds: ids });
  }

  hide(): void {
    this.state.set(null);
  }
}
